import React, { createContext, useState, useContext, useMemo, useEffect } from 'react';
import {
  ACCESS_TOKEN_NAME,
  PARTNER_ID,
  ROLE_TYPES,
  USER_ROLE,
  PARENT_ID,
  MANAGING_PARTNER_CHILDS,
} from 'api/constants';
import { useNavigate } from 'react-router-dom';
import { getRoleFromHostname } from 'api/roleConfig';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const initialRole = getRoleFromHostname(hostname);

  const isToken = localStorage.getItem(ACCESS_TOKEN_NAME[initialRole]);
  const partnerID = localStorage.getItem(PARTNER_ID);
  const parentID = localStorage.getItem(PARENT_ID);
  const managingPartnerChilds = localStorage.getItem(MANAGING_PARTNER_CHILDS);
  const childrenOfParent = managingPartnerChilds ? JSON.parse(managingPartnerChilds) : null;

  const [auth, setAuth] = useState(!!isToken);
  const [role, setRole] = useState(initialRole);
  const [partnerId, setPartnerId] = useState(partnerID);
  const [parentId, setParentId] = useState(parentID);
  const [partnerChilds, setPartnerChilds] = useState(childrenOfParent);
  const [isParentManaging, setIsParentManaging] = useState(false);

  useEffect(() => {
    if (partnerId && parentId && +partnerId !== +parentId) {
      return setIsParentManaging(true);
    }

    return setIsParentManaging(false);
  }, [parentId, partnerId]);

  // Keep session/window/localStorage in sync whenever the resolved role changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (window.name !== role) window.name = role;
    } catch (e) {
      /* ignore */
    }
    const current = sessionStorage.getItem(USER_ROLE);
    if (current !== role) sessionStorage.setItem(USER_ROLE, role);
    const localCurrent = localStorage.getItem(USER_ROLE);
    if (localCurrent !== role) localStorage.setItem(USER_ROLE, role);
  }, [role]);

  const login = (token, role) => {
    localStorage.setItem(ACCESS_TOKEN_NAME[role], token);
    setAuth(true);
    setRole(role);
    if (typeof window !== 'undefined') window.name = role;
    sessionStorage.setItem(USER_ROLE, role);
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      try {
        window.dispatchEvent(new CustomEvent('pf25:clearUserDetails'));
      } catch (e) {
        /* empty */
      }
    }
  };

  const logout = () => {
    localStorage.removeItem(ACCESS_TOKEN_NAME[role]);
    if (typeof window !== 'undefined') window.name = '';
    if (role === ROLE_TYPES.slh_manager) {
      localStorage.removeItem(PARTNER_ID);
    }
    setIsParentManaging(false);
    setAuth(false);
    if (partnerID) {
      setPartnerId(null);
      localStorage.removeItem(PARTNER_ID);
    }
    if (parentID) {
      setParentId(null);
      localStorage.removeItem(PARENT_ID);
    }
    if (childrenOfParent) {
      setPartnerChilds(null);
      localStorage.removeItem(MANAGING_PARTNER_CHILDS);
    }

    if (role === ROLE_TYPES.admin || role === ROLE_TYPES.slh_manager) {
      return navigate('/');
    }
    return navigate('/login');
  };

  const partnerIDSetter = (partner_id) => {
    localStorage.setItem(PARTNER_ID, partner_id);
    setPartnerId(partner_id);
  };

  const parentIDSetter = (parent_id) => {
    localStorage.setItem(PARENT_ID, parent_id);
    setParentId(parent_id);
  };

  const setPartnerChildren = (childs) => {
    localStorage.setItem(MANAGING_PARTNER_CHILDS, JSON.stringify(childs));
    setPartnerChilds(childs);
  };

  const value = useMemo(
    () => ({
      auth,
      login,
      logout,
      role,
      partnerIDSetter,
      partnerId,
      parentId,
      parentIDSetter,
      partnerChilds,
      isParentManaging,
      setPartnerChildren,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auth, role, partnerId, parentId, partnerChilds, isParentManaging],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
